import { useEffect, useMemo } from 'react';

import { useDispatch } from 'react-redux';

import type { GetMeritBadgesQueryParams, MeritBadge } from '@appTypes/esb';
import {
  useGetDictAkelaMeritBadgesQuery,
  useGetDictMeritBadgesQuery,
} from '@dict/dict-api';
import { dictionaryIds } from '@shared/constants';

import { dictionariesResponse } from './actions';

/** To provide backwards compatibility with request in epics */
function normalizeMBs(mbs?: MeritBadge[]) {
  if (!mbs) return [];

  return (
    mbs
      // this is how V1 was doing it, probably makes more sense to do it in selector
      .filter(mb => !(mb.adminNotes || '').includes('council'))
      .map(mb => ({
        ...mb,
        /** Same as `eagleRequired`. In V2 this comes now as a boolean, before was string */
        isEagleRequired: !!mb.eagleRequired,
      }))
  );
}

export type NormalizedMeritBadge = ReturnType<typeof normalizeMBs>[number];

/**
 * Used to replace old requests of dictionaries in epics.
 * Currently it works both ways but as TODO: we'll remove the
 * merit badges request in the epic.
 */
export function useMBDict(
  props: { skip?: boolean; params?: GetMeritBadgesQueryParams } = {
    skip: false,
  },
) {
  const dispatch = useDispatch();
  const response = useGetDictMeritBadgesQuery(
    { params: props.params },
    {
      skip: props.skip,
    },
  );

  const meritBadges = useMemo(
    () =>
      response.data?.meritBadges
        ? normalizeMBs(response.data?.meritBadges)
        : [],
    [response.data],
  );

  /** Merit Badges dict indexed by id */
  const meritBadgesById = useMemo(
    () => new Map(meritBadges?.map(mb => [mb.id, mb])),
    [meritBadges],
  );

  useEffect(() => {
    if (props.skip) return;

    dispatch(
      dictionariesResponse({
        [dictionaryIds.MERIT_BADGES]: meritBadges,
      }),
    );
  }, [dispatch, meritBadges, props.skip]);

  return {
    response,
    /** Normalized array, use this or the selector from the dict module */
    meritBadges,
    meritBadgesById,
  };
}

export function useAkelaMBDict(
  props: { skip?: boolean } = {
    skip: false,
  },
) {
  const response = useGetDictAkelaMeritBadgesQuery(undefined, {
    skip: props.skip,
  });

  const meritBadges = useMemo(
    () => (response.data ? response.data : []),
    [response.data],
  );

  return {
    response,
    meritBadges,
  };
}
